import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Slider } from "../components/ui/slider";
import { CategoryTreeFilter } from "../components/CategoryTreeFilter";
import { buildCategoryTree, ancestorIds } from "../lib/categoryTree";

export default function Browse() {
  const [sp, setSp] = useSearchParams();
  const q = sp.get("q") || "";
  const category = sp.get("category") || "";
  const sort = sp.get("sort") || "newest";
  const [priceRange, setPriceRange] = useState([
    Number(sp.get("min_price") || 0), Number(sp.get("max_price") || 200),
  ]);
  const [language, setLanguage] = useState(sp.get("language") || "any");
  const [cats, setCats] = useState([]);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)); }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (category) params.category = category;
    if (priceRange[0] > 0) params.min_price = priceRange[0];
    if (priceRange[1] < 200) params.max_price = priceRange[1];
    if (language && language !== "any") params.language = language;
    if (sort) params.sort = sort;
    let stale = false;
    const timer = setTimeout(() => {
      api.get("/products", { params }).then((r) => { if (!stale) setData(r.data); }).finally(() => { if (!stale) setLoading(false); });
    }, 200);
    return () => { stale = true; clearTimeout(timer); };
  }, [q, category, sort, priceRange, language]);

  const setParam = (key, val) => {
    const next = new URLSearchParams(sp);
    if (val) next.set(key, val); else next.delete(key);
    setSp(next);
  };

  const tree = React.useMemo(() => buildCategoryTree(cats), [cats]);
  const [open, setOpen] = useState({});
  const toggle = (id) => setOpen((o) => ({ ...o, [id]: !o[id] }));
  useEffect(() => {
    const sel = cats.find((c) => c.slug === category);
    if (sel) setOpen((o) => ({ ...o, ...Object.fromEntries(ancestorIds(cats, sel.id).map((id) => [id, true])) }));
  }, [cats, category]);

  return (
    <div className="container-page py-10" data-testid="browse-page">
      <div className="flex items-end justify-between border-b border-border pb-4 mb-8 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-4xl tracking-tight">
            {q ? <>Results for &quot;<span className="italic">{q}</span>&quot;</> : category ? cats.find(c => c.slug === category)?.name || "Catalogue" : "All titles"}
          </h1>
          <p className="text-sm text-muted-foreground mt-1 italic">{data.total} title{data.total === 1 ? "" : "s"}</p>
        </div>
        <Select value={sort} onValueChange={(v) => setParam("sort", v)}>
          <SelectTrigger className="w-48 rounded-sm" data-testid="sort-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Newest</SelectItem>
            <SelectItem value="bestsellers">Bestsellers</SelectItem>
            <SelectItem value="price_asc">Price: low to high</SelectItem>
            <SelectItem value="price_desc">Price: high to low</SelectItem>
            <SelectItem value="rating">Top rated</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <aside className="lg:col-span-3 space-y-8" data-testid="filters-panel">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-muted-foreground mb-3">Category</div>
            <CategoryTreeFilter tree={tree} selected={category} open={open} toggle={toggle} onPick={(slug) => setParam("category", slug)} />
          </div>
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-muted-foreground mb-3">Price</div>
            <Slider min={0} max={200} step={1} value={priceRange} onValueChange={setPriceRange} data-testid="price-slider" />
            <div className="flex justify-between text-xs text-muted-foreground mt-2"><span>A${priceRange[0]}</span><span>A${priceRange[1]}</span></div>
          </div>
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-muted-foreground mb-3">Language</div>
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger className="rounded-sm" data-testid="filter-language"><SelectValue placeholder="Any" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any</SelectItem>
                <SelectItem value="English">English</SelectItem>
                <SelectItem value="Spanish">Spanish</SelectItem>
                <SelectItem value="French">French</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </aside>

        <div className="lg:col-span-9">
          {loading ? <div className="text-sm text-muted-foreground">Loading…</div> :
           data.items.length === 0 ? (
            <div className="text-center py-24 border border-dashed border-border rounded-sm">
              <div className="font-display text-3xl">Nothing here yet.</div>
              <div className="text-sm text-muted-foreground mt-2 italic">Try clearing your filters.</div>
            </div>
          ) : (
            <div className="product-grid" data-testid="browse-grid">{data.items.map((p) => <ProductCard key={p.id} product={p} />)}</div>
          )}
        </div>
      </div>
    </div>
  );
}
